// Centralized config for the SmartBiz Toolkit app.
//
// Today the API base URLs are hardcoded inside each tool's API client
// (imageStudioApi.js, blissApi.js, etc.). Eventually those clients should
// import from here, but during the migration we leave them as-is to avoid
// a per-tool refactor. This file is the new home and the audit-log of where
// the toolkit talks to.
//
// Each Lambda has its own API Gateway. They share a Cognito User Pool, so
// once Phase 3 ships, the same JWT works against all of them.

export const API_HOSTS = {
  // ap-south-1 endpoints
  imageStudio: 'https://css2g9ujye.execute-api.ap-south-1.amazonaws.com',
  leadEnrichment: 'https://w74ji2gjnb.execute-api.ap-south-1.amazonaws.com',
  icFileConverter: 'https://s8ngo3lrv8.execute-api.ap-south-1.amazonaws.com',
  listingOptimizer: 'https://ccmvpzpld0.execute-api.ap-south-1.amazonaws.com',
  listingCreation: 'https://b5bpqa285b.execute-api.ap-south-1.amazonaws.com',
  imageLinkGenerator: 'https://zs58uzhuck.execute-api.ap-south-1.amazonaws.com',
  productImageGen: 'https://ah7mrbv78e.execute-api.ap-south-1.amazonaws.com',
  // Store CRO Audit — populated once `deploy_api.sh` has stood up the API
  // Gateway and we have a real ID. Until then VITE_STORE_CRO_AUDIT_API in
  // .env.local can override; the tool's API client also has a fallback URL.
  storeCROAudit: import.meta.env.VITE_STORE_CRO_AUDIT_API || 'https://kb3jqk3xi9.execute-api.ap-south-1.amazonaws.com',
  // AI Chat's knowledge admin CRUD (GET/PATCH/DELETE /api/knowledge) — a
  // plain HTTP API, separate from the WebSocket chat endpoint below.
  aiChatAdmin: import.meta.env.VITE_AI_CHAT_ADMIN_API || 'https://oixkjy4nlf.execute-api.ap-south-1.amazonaws.com',
  // AI Chat's Dashboards CRUD/refresh/share (4 Aug 2026) — a separate
  // plain HTTP API from both the WS chat endpoint and the knowledge
  // admin API above (locked decision #8, ai-chat-dashboard-tracker.md).
  aiChatDashboards: import.meta.env.VITE_AI_CHAT_DASHBOARDS_API || 'https://8gjezysdtl.execute-api.ap-south-1.amazonaws.com',
  // Insight reports — left here for completeness; the new app doesn't route
  // to those pages, but the AdoptionTracker component on Home does call this
  // host (it shares the image-studio API for the adoption stats endpoint).
  insights: 'https://aupwhapge6.execute-api.ap-south-1.amazonaws.com',
};

// AI Chat's WebSocket endpoint. Separate from API_HOSTS because it's a
// wss:// URL, not an https:// one — the id_token travels as a `?token=`
// query param on the connection URL itself (WebSocket handshakes can't
// carry custom headers), verified server-side by a Lambda REQUEST
// authorizer (see smartbiz-toolkit/tools/ai-chat/authorizer/handler.py).
export const AI_CHAT_WS_URL = import.meta.env.VITE_AI_CHAT_WS_URL
  || 'wss://uwkqtxunfl.execute-api.ap-south-1.amazonaws.com/prod';

// Cognito config — read from VITE_ env vars. In dev mode (no Cognito yet)
// these can be undefined and AuthProvider falls back to a hardcoded user.
export const COGNITO = {
  authority: import.meta.env.VITE_COGNITO_AUTHORITY,
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
  domain: import.meta.env.VITE_COGNITO_DOMAIN,
  redirectUri: import.meta.env.VITE_COGNITO_REDIRECT_URI || `${window.location.origin}/auth/callback`,
  logoutUri: import.meta.env.VITE_COGNITO_LOGOUT_URI || window.location.origin,
};

// Dev-mode auth bypass — flip to false for any deployed environment.
// Phase 1 uses this to get the app running before Cognito exists. Phase 2
// flips it off and the real Cognito flow kicks in.
export const AUTH_DEV_MODE = import.meta.env.VITE_AUTH_DEV_MODE === 'true';
export const AUTH_DEV_USERNAME = import.meta.env.VITE_AUTH_DEV_USERNAME || 'devuser';
// Dev-mode admin simulation — opt in to test admin-only screens locally
// without hitting Cognito. Defaults to false so a fresh dev session matches
// the most common production case (non-admin user).
export const AUTH_DEV_IS_ADMIN = import.meta.env.VITE_AUTH_DEV_IS_ADMIN === 'true';

// Sanity check on app boot. If we're not in dev mode and Cognito vars are
// missing, fail loud rather than silently rendering a broken login flow.
export function assertConfigValid() {
  if (AUTH_DEV_MODE) return;
  const missing = [];
  if (!COGNITO.authority) missing.push('VITE_COGNITO_AUTHORITY');
  if (!COGNITO.clientId) missing.push('VITE_COGNITO_CLIENT_ID');
  if (!COGNITO.domain) missing.push('VITE_COGNITO_DOMAIN');
  if (missing.length) {
    throw new Error(
      `Cognito config missing: ${missing.join(', ')}. ` +
      `Set these in .env.local or set VITE_AUTH_DEV_MODE=true for local dev without Cognito.`
    );
  }
}
